import { useState, type ReactNode } from "react"

import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import vendorApp from "~/assets/photo/solar-proposal/vendor-app.jpg"
import vendorNode from "~/assets/photo/solar-proposal/vendor-node.jpg"

import { formatStep } from "./ai-compute-format"
import { Eyebrow, Panel } from "../../BatteryOnlyProposal/primitives"
import { formatUsdExact } from "../../BatteryOnlyProposal/view-helpers"

/**
 * The pieces the Solar and Battery AI compute sections share: ledger
 * rows, stat tiles, the share split, the
 * how-it-works list, the FAQ and the Vendor app card. Each section brings its own copy and figures.
 */

export function LedgerRow({
  label,
  note,
  value,
  tone,
  rule = false,
  tag = null,
}: {
  label: string
  note: string
  value: string
  tone?: `danger` | `success`
  rule?: boolean
  /** A chip beside the label, e.g. "Guaranteed". */
  tag?: ReactNode
}): JSX.Element {
  return (
    <div
      className={clsx(
        `flex items-baseline justify-between gap-4 border-t py-3.5`,
        rule ? `border-bp-border-strong` : `border-bp-border`,
      )}
    >
      <div>
        <div className="text-[14.5px] font-medium text-bp-text">
          {label}
          {tag ? <> {tag}</> : null}
        </div>
        <div className="mt-0.5 text-[12px] text-bp-text-dim">{note}</div>
      </div>
      <div
        className={clsx(
          `font-display text-[17px] font-semibold tracking-[-0.02em] tabular-nums`,
          tone === `danger`
            ? `text-bp-danger`
            : tone === `success`
              ? `text-bp-success`
              : `text-bp-text`,
        )}
      >
        {value}
      </div>
    </div>
  )
}

export type ShareSplitPart = {
  label: string
  value: number
  /** The bar segment's and key's background class. */
  swatch: string
}

/**
 * The monthly share split at a cost, drawn to scale with each part's figure
 * beneath. The parts are the gateway's split; nothing here derives money.
 */
export function ShareSplit({
  parts,
}: {
  parts: readonly ShareSplitPart[]
}): JSX.Element {
  return (
    <>
      {/* The figures below carry the split for assistive tech. */}
      <div
        aria-hidden="true"
        className="mt-1 flex h-3 gap-0.5 overflow-hidden rounded-full bg-bp-elevated"
      >
        {parts.map(({ label, value, swatch }) =>
          value > 0 ? (
            <div
              key={label}
              className={clsx(`basis-0`, swatch)}
              style={{ flexGrow: value }}
            />
          ) : null,
        )}
      </div>
      <dl
        className={clsx(
          `grid grid-cols-2 gap-4`,
          parts.length > 2 && `sm:grid-cols-3`,
        )}
      >
        {parts.map(({ label, value, swatch }) => (
          <div key={label}>
            <dt className="flex items-center gap-2 text-[13px] text-bp-text-muted">
              <span
                aria-hidden="true"
                className={clsx(`size-2.5 rounded-sm`, swatch)}
              />
              {label}
            </dt>
            <dd className="font-display mt-1 text-[length:var(--step-2)] leading-none font-semibold tracking-[-0.02em] text-bp-text tabular-nums">
              {formatUsdExact(value)}
            </dd>
          </div>
        ))}
      </dl>
    </>
  )
}

export function Stat({
  label,
  value,
  note,
}: {
  label: string
  value: ReactNode
  note: string
}): JSX.Element {
  return (
    <div className="border-bp-border sm:border-l sm:pl-5 sm:first:border-l-0 sm:first:pl-0">
      <Eyebrow>{label}</Eyebrow>
      <div className="font-display mt-1.5 text-[length:var(--step-2)] leading-none font-semibold tracking-[-0.02em] text-bp-text tabular-nums">
        {value}
      </div>
      <div className="mt-1.5 text-[12px] text-bp-text-muted">{note}</div>
    </div>
  )
}

/** The node's photo with its name, package line and an optional pill beneath (the Hosting Plan). */
export function NodeHeroPanel({
  name,
  specs,
  testId,
  badge = null,
}: {
  name: string
  specs: string | null
  testId: string
  badge?: ReactNode
}): JSX.Element {
  return (
    <Panel className="relative min-h-[320px] overflow-hidden rounded-[20px] lg:min-h-[440px]">
      <img
        src={vendorNode}
        alt="Vendor compute node, wall-mounted"
        className="absolute inset-0 size-full object-cover object-[60%_50%]"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_50%,rgba(5,7,13,0.88)_100%)]" />
      <div className="absolute inset-x-6 bottom-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-1.5 text-[11px] tracking-[0.14em] text-white/75 uppercase">
            Vendor® compute node
          </div>
          <div
            data-testid={testId}
            className="font-display text-[26px] font-semibold tracking-[-0.02em] text-white"
          >
            {name}
          </div>
          {specs ? (
            <div className="mt-0.5 text-[13px] text-white/80">{specs}</div>
          ) : null}
          {badge ? (
            <div className="mt-2.5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-[rgba(5,7,13,0.6)] px-3 py-1 text-[12px] font-semibold text-white">
              <span
                aria-hidden="true"
                className="size-2 rounded-full bg-bp-success"
              />
              {badge}
            </div>
          ) : null}
        </div>
        <div className="rounded-full border border-white/20 bg-[rgba(5,7,13,0.6)] px-3 py-1.5 text-[12px] whitespace-nowrap text-white">
          In partnership with Vendor®
        </div>
      </div>
    </Panel>
  )
}

export type AiComputeStep = { title: string; body: string }

export function HowItWorksPanel({
  steps,
  footer,
}: {
  steps: readonly AiComputeStep[]
  footer: ReactNode
}): JSX.Element {
  return (
    <Panel className="flex flex-col rounded-[20px] p-6 lg:p-8">
      <Eyebrow className="mb-5">How it works</Eyebrow>
      <ol className="flex-1">
        {steps.map(({ title, body }, index) => (
          <li
            key={title}
            className="grid grid-cols-[44px_1fr] gap-3.5 border-t border-bp-border py-4 first:border-t-0"
          >
            <span className="pt-0.5 font-mono text-[13px] text-bp-accent">
              {formatStep(index)}
            </span>
            <div>
              <div className="font-display text-[19px] font-semibold tracking-[-0.015em] text-bp-text">
                {title}
              </div>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-bp-text-muted">
                {body}
              </p>
            </div>
          </li>
        ))}
      </ol>
      <div className="border-t border-bp-border pt-3.5 text-[12px] text-bp-text-dim">
        {footer}
      </div>
    </Panel>
  )
}

export type AiComputeFaqEntry = { question: string; answer: string }

export function AiComputeFaq({
  entries,
  idPrefix,
}: {
  entries: readonly AiComputeFaqEntry[]
  /** Answer element ids: `${idPrefix}-faq-${index}`. */
  idPrefix: string
}): JSX.Element {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <Panel className="mt-3.5 rounded-[20px] p-6 lg:p-8">
      <Eyebrow className="mb-2">Questions homeowners ask</Eyebrow>
      <div className="grid grid-cols-1 items-start gap-x-12 lg:grid-cols-2">
        {entries.map(({ question, answer }, index) => {
          const isOpen = openIndex === index
          const answerId = `${idPrefix}-faq-${index}`
          return (
            <div key={question} className="border-t border-bp-border">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={answerId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="flex w-full items-center justify-between gap-4 py-4 text-left"
              >
                <span className="font-display text-[16px] font-semibold tracking-[-0.01em] text-bp-text">
                  {question}
                </span>
                <ChevronDownIcon
                  aria-hidden="true"
                  className={clsx(
                    `size-4 shrink-0 text-bp-text-muted transition-transform print:hidden`,
                    isOpen && `rotate-180`,
                  )}
                />
              </button>
              <p
                id={answerId}
                className={clsx(
                  `mr-10 mb-4 text-[13.5px] leading-relaxed text-bp-text-muted`,
                  isOpen ? `block` : `hidden print:block`,
                )}
              >
                {answer}
              </p>
            </div>
          )
        })}
      </div>
    </Panel>
  )
}

export function VendorAppCard({
  title = `Track your base, bonus and payouts in the Vendor app.`,
}: {
  title?: string
} = {}): JSX.Element {
  return (
    <Panel className="grid grid-cols-[1fr_minmax(120px,200px)] items-stretch overflow-hidden rounded-[20px]">
      <div className="flex flex-col justify-center p-6 lg:p-8">
        <Eyebrow className="mb-2.5">Earnings in view</Eyebrow>
        <div className="font-display text-[length:var(--step-1)] leading-tight font-semibold tracking-[-0.015em] text-bp-text">
          {title}
        </div>
      </div>
      <img
        src={vendorApp}
        alt="Vendor app showing compute earnings"
        className="block size-full object-cover object-[45%_30%]"
      />
    </Panel>
  )
}
